import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';

/**
 * Turns a failure into a sentence in the user's language.
 *
 * The server's `code` is the contract; its `message` is a developer-facing
 * fallback that is not translated. Without this an English UI would surface
 * Chinese error text straight from the API.
 */
export function useApiError() {
  const { t, i18n } = useTranslation();

  return useCallback(
    (error: unknown, fallbackKey = 'errors.unknown_error'): string => {
      const code = error instanceof ApiError ? error.code : null;
      if (code && i18n.exists(`errors.${code}`)) return t(`errors.${code}`);
      // An unmapped code is a gap in the dictionary, not something to hide —
      // showing the server's own wording beats showing nothing.
      if (error instanceof ApiError && error.message) return error.message;
      return t(fallbackKey);
    },
    [t, i18n],
  );
}
